import React, { useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi, RCCSwitch, RCCTextArea } from "react-common-components";
import { useForm } from "react-hook-form";
import Audit from "src/components/Audit";
import DeleteModal from "src/components/DeleteModal";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { IMdtba001, IModalAddOrChangeBusinessDiagnostics, IRefBusinessDiagnosticsModal } from "./interface";
import {addAndChangeSchema, deleteSchema } from "./schemas";


const ModalAddChangeDeleteKanban: React.ForwardRefRenderFunction<IRefBusinessDiagnosticsModal, IModalAddOrChangeBusinessDiagnostics> = ({ 
	onCallbackAdd,
	onCallbackChange,
	onCallbackDelete
}, ref) => {

	const { translate } = useTranslation();
	const [selectedModel, setSelectedModel] = useState<IMdtba001>({} as IMdtba001);
	const [isAdd, setIsAdd] = useState(false);

	const refModalAddOrChange = useRef<RCCRefModalProps>(null);
	const modalDelete = useRef<RCCRefModalProps>(null);

	const { addToast } = RCCHookToast();

	const { 
		handleSubmit, 
		register, 
		reset,
		setError, 
		formState: {errors},
		clearErrors } = useForm();

	const { 
		handleSubmit: handleSubmitDelete, 
		register: registerDelete, 
		reset : resetDelete, 
		setError : setErrorDelete, 
		formState: { errors : errorsDelete }, 
		clearErrors: clearErrorsDelete 
	} = useForm();
	

	const actionButtonAdd = () => {
		reset();
		setIsAdd(true);
		setSelectedModel({} as IMdtba001);
		refModalAddOrChange.current?.openModal();
	};

	const actionButtonChange = (mdtba001: IMdtba001) => {
		reset();
		setIsAdd(false);
		setSelectedModel(mdtba001);
		refModalAddOrChange.current?.openModal();
	};

	const actionButtonDelete = (mdtba001: IMdtba001) => {
		resetDelete();
		setSelectedModel(mdtba001);
		modalDelete.current?.openModal();
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));

	const onSubmit = async (data: any) => {
		try{
			clearErrors();

			await addAndChangeSchema(translate).validate({...data},{
				abortEarly: false
			});

			if(isAdd){
				await RCCServicesApi.postRequest({
					method: "DiagnosticModels/Insert",
					body: {
						...data,
						mda001Ativo: data.mda001Ativo ? "S": "N"
					}
				});

				if(onCallbackAdd)
					onCallbackAdd();

			}else{
				await RCCServicesApi.postRequest({
					method: "DiagnosticModels/Update",
					body: {
						...data,
						mda001Id: selectedModel.mda001Id,
						mda001Ativo: data.mda001Ativo ? "S": "N"
					}
				});

				if(onCallbackChange)
					onCallbackChange();
			}

			refModalAddOrChange.current?.closeModal();

		}catch(err){
			refModalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setError)){
				addToast({
					title: translate("Erro"),
					description: translate("Verifique os erros no formulário e tente novamente"),
					color: "yellow"
				});
				return;
			}

			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar modelo de diagnóstico") : translate("Erro ao alterar modelo de diagnóstico"), addToast);
		}
	};

	const submitDelete = async (data: any) => {
		try{
			clearErrorsDelete();	
			modalDelete.current?.setLoadingButton();

			await deleteSchema(translate).validate({...data, "mda001TituloConfirmation": selectedModel.mda001Titulo},{
				abortEarly: false
			});
			
			await RCCServicesApi.deleteRequest({
				method: "DiagnosticModels/Delete",
				urlParameters: {
					id: selectedModel.mda001Id
				}
			});

			addToast({
				color: "green",
				description: translate("Modelo de diagnóstico excluído com sucesso"),
				title: translate("Sucesso")
			});
			modalDelete.current?.resetLoadingButton();
			modalDelete.current?.closeModal();

			if(onCallbackDelete)
				onCallbackDelete(selectedModel.mda001Id);

		}catch(err){
			modalDelete.current?.resetLoadingButton();

			if(getValidationErrors(err, setErrorDelete))
				return;

			standardErrorMessages(err, translate("Erro ao excluir modelo de diagnóstico"), addToast);
		}
		
	};

	return (
		<>
			<DeleteModal
				ref={modalDelete}
				title= {translate("Exclusão de modelo de diagnóstico")}
				description= {`${translate("Deseja realmente excluir o modelo de diagnóstico")}: ${selectedModel.mda001Titulo}?`}
				onSubmit = {handleSubmitDelete(submitDelete)} 
				height="250px"
			>
				<RCCGrid
					container
					item
					xs={10}
				>
					<RCCInput 
						register={registerDelete}
						name="mda001Titulo"
						label={translate("Digite o título do modelo de diagnóstico para excluí-lo")}
						maxLength={40}
						error={errorsDelete.mda001Titulo}
						required
					/>
				</RCCGrid>
			</DeleteModal>

			<Modal
				ref={refModalAddOrChange}
				onSubmit={handleSubmit(onSubmit)}
				header={{
					title: isAdd ? translate("Adicionar modelo de diagnóstico"): translate("Alterar modelo de diagnóstico"),
					subtitle: !isAdd ? `${translate("Modelo de diagnóstico")}: ${selectedModel.mda001Titulo}`: "",
					icon: {
						name: isAdd? "plus": "pencil"
					}
				}}
				styleInline={ModalSide}
			>

				<RCCGrid 
					container 
					item 
					direction="column"
					alignItems="center"
				>
					<RCCGrid 
						container
						item
						xs={12}
						sm={10}
						alignItems="center"
						justify="flex-end"
					>
						<Audit data={selectedModel} type="pop"/>
					</RCCGrid>
					<RCCGrid 
						container 
						item
						xs={12}
						sm={10}
					>
						<RCCInput 
							name="mda001Titulo"
							register={register}
							label={translate("Título")}
							defaultValue={selectedModel.mda001Titulo ?? ""}
							error={errors?.mda001Titulo}
							maxLength={40}
							required
						/>
					</RCCGrid>
					<RCCGrid 
						container 
						item
						xs={12}
						sm={10}
					>	
						<RCCTextArea 
							register={register}
							label={translate("Descrição")}
							name="mda001Descricao"
							error={errors.mda001Descricao}
							defaultValue={selectedModel.mda001Descricao}
							maxLength={4000}
							required
						/>
					</RCCGrid>
					<RCCGrid 
						container 
						item
						xs={12}
						sm={10}
					>		
						<RCCSwitch
							name="mda001Ativo"
							label={translate("Ativo")}
							register={register}
							defaultChecked={selectedModel.mda001Ativo === "S" || isAdd}
						/>
					</RCCGrid>
				</RCCGrid>
									
			</Modal>
		
		</>
	);
};


export default React.forwardRef(ModalAddChangeDeleteKanban);
